import { getTimezone } from "@/lib/timezone";

import { SignedPreviewResponse } from "@/types/billing";
import { Unit } from "@/types/enums/unit";
import type { NoteSuppliesState } from "@/types/forms/question";
import type { DetachedChartDoses } from "@/types/medication";
import {
  AttachmentDayPage,
  DynamicFieldSummary,
  NoteAnswerDetail,
  NoteAttachment,
  NoteAttachmentResponse,
  NoteAttachmentTag,
  NoteAutoSignOffProvider,
  NoteBulkSignOffRequest,
  NoteBulkSignOffResult,
  NoteCreate,
  NoteDetail,
  NoteGroupItem,
  NoteLatestRevision,
  NoteListItem,
  NoteMappingsResponse,
  NoteScribeResponse,
  NoteSignOffCandidate,
  NoteSignOffSignerGroup,
  NoteSummary,
  NoteTaskSuggestionSet,
  NoteTemplateCreate,
  NoteTemplateTableItem,
  NoteTemplateUpdate,
  NoteTranscriptionResponse,
  NotesFetchParams,
  PrefilledAnswersResponse,
  TemplateAttachmentUploadResponse,
} from "@/types/notes";
import { Page } from "@/types/pagination";

import { requestData } from "@/api/api-config";

/**
 * `timezone` is the zone `createdDate`/`visitDate` days are read in; pass it
 * when the caller built those days from a clock of its own.
 */
export async function getNotes(
  params: NotesFetchParams,
  timezone: string = getTimezone(),
): Promise<Page<NoteListItem> | null> {
  return await requestData<Page<NoteListItem>>(
    "notes/list",
    "get",
    {
      searchParams: { ...params, tz: timezone },
    },
    false,
  );
}

export async function getNoteDetail(
  noteId: string,
): Promise<NoteDetail | undefined> {
  const tz = getTimezone();
  return (
    (await requestData<NoteDetail>(
      `notes/${noteId}/detail`,
      "get",
      { searchParams: { tz } },
      false,
    )) ?? undefined
  );
}

export async function getLatestNoteRevision(
  noteId: string,
): Promise<NoteLatestRevision | null> {
  return await requestData<NoteLatestRevision>(
    `notes/${noteId}/latest`,
    "get",
    // Chrome answers requests made right after Back from its HTTP cache, even
    // without cache headers, which would hand back the very version that's stale.
    { cache: "no-store" },
    false,
  );
}

export async function startRecordingSession(
  patientId: string,
  locationId?: string,
  overrideOptOut?: boolean,
  eventId?: string | null,
  noteTemplateId?: string | null,
): Promise<NoteDetail | null> {
  const formData = new FormData();
  formData.append("patient_id", patientId);
  if (locationId) {
    formData.append("location_id", locationId);
  }
  if (overrideOptOut) {
    formData.append("override_opt_out", "true");
  }
  if (eventId) {
    formData.append("event_id", eventId);
  }
  if (noteTemplateId) {
    formData.append("note_template_id", noteTemplateId);
  }

  return await requestData<NoteDetail>(
    "notes/start-recording",
    "post",
    {
      body: formData,
    },
    true,
  );
}

export async function saveAiScribeConsent(
  patientId: string,
  consent: boolean,
  noteId?: string,
): Promise<{ patientId: string; consent: boolean } | null> {
  return await requestData<{ patientId: string; consent: boolean }>(
    "notes/ai-scribe-consent",
    "post",
    { json: { patientId, consent, noteId } },
    false,
  );
}

export async function transcribeNote(
  audio: File,
  patientId: string,
  providerId: string,
  noteId: string,
  locationId?: string,
): Promise<NoteTranscriptionResponse | null> {
  const formData = new FormData();
  formData.append("audio_file", audio, "recording.webm");
  formData.append("patient_id", patientId);
  formData.append("provider_id", providerId);
  formData.append("note_id", noteId);
  if (locationId) {
    formData.append("location_id", locationId);
  }

  // catchError: false so HTTP errors are thrown and can be properly classified
  // by the transcription queue (e.g., 400 "Note already completed" = permanent failure)
  return await requestData<NoteTranscriptionResponse>(
    "notes/transcribe",
    "post",
    {
      body: formData,
      timeout: 5 * 60 * 1000,
    },
    false,
  );
}

export async function createAiNote(
  patientId: string,
  providerId: string,
  noteId: string,
  noteTemplateId?: string,
): Promise<NoteDetail | null> {
  const tz = getTimezone();
  return await requestData<NoteDetail>("notes/scribe", "post", {
    searchParams: { tz },
    json: {
      patientId,
      providerId,
      noteId,
      noteTemplateId,
    },
    // 5 minutes
    timeout: 60 * 1000 * 5,
  });
}

export async function chatWithAiNote(
  parentNoteId: string,
  providerId: string,
  message: string,
): Promise<NoteScribeResponse | null> {
  return await requestData<NoteScribeResponse>("notes/chat", "post", {
    json: {
      parentNoteId,
      providerId,
      message,
    },
    // 5 minutes
    timeout: 60 * 1000 * 5,
  });
}

export async function getNoteSupplies(
  noteId: string,
  questionId: string,
): Promise<NoteSuppliesState | null> {
  return await requestData<NoteSuppliesState>(
    "notes/supplies",
    "get",
    { searchParams: { noteId, questionId } },
    false,
  );
}

export async function updateNoteAnswers(
  noteId: string,
  answers: Array<{
    id: string;
    data: Record<string, any>;
    note_attachment_id?: string;
  }>,
): Promise<NoteAnswerDetail[] | null> {
  return await requestData<NoteAnswerDetail[]>(
    `notes/${noteId}/answers`,
    "put",
    {
      json: { answers },
      timeout: 60 * 1000 * 5,
    },
  );
}

export async function updateNoteTitle(
  noteId: string,
  title: string,
): Promise<{ id: string; title: string } | null> {
  return await requestData<{ id: string; title: string }>(
    `notes/${noteId}/title`,
    "patch",
    {
      json: { title },
    },
  );
}

export async function updateNoteEventLink(
  noteId: string,
  eventId: string | null,
): Promise<{ id: string; eventId: string | null } | null> {
  return await requestData<{ id: string; eventId: string | null }>(
    `notes/${noteId}/event`,
    "patch",
    {
      json: { eventId },
    },
  );
}

export async function updateNoteTemplateId(
  noteId: string,
  templateId: string,
): Promise<{ id: string; templateId: string } | null> {
  // catchError=false so HTTP/network failures reject instead of resolving to
  // null. The persistence is fire-and-forget; without this the mutation's
  // onError toast never fires and a failed save looks successful until reload.
  return await requestData<{ id: string; templateId: string }>(
    `notes/${noteId}/template`,
    "patch",
    {
      json: { templateId },
    },
    false,
  );
}

export interface NoteTemplatePatientViewUpdate {
  visibleToPatient: boolean;
  patientVisibleQuestionOrder: string[] | null;
  showSummaryInPatientView: boolean;
}

export async function updateNoteTemplatePatientView(
  noteTemplateId: string,
  payload: NoteTemplatePatientViewUpdate,
): Promise<NoteTemplateTableItem | null> {
  return await requestData<NoteTemplateTableItem>(
    `notes/templates/${noteTemplateId}/patient-view`,
    "patch",
    {
      json: payload,
    },
  );
}

export async function getNoteRevisions(
  parentNoteId: string,
): Promise<NoteSummary[] | null> {
  return await requestData<NoteSummary[]>(
    `notes/${parentNoteId}/revisions`,
    "get",
  );
}

export async function getNoteMappings(
  noteId: string,
): Promise<NoteMappingsResponse | null> {
  return await requestData<NoteMappingsResponse>(
    `notes/${noteId}/mappings`,
    "get",
  );
}

export async function getNoteRevision(
  parentNoteId: string,
  version: number,
): Promise<NoteDetail | null> {
  return await requestData<NoteDetail>(
    `notes/${parentNoteId}/${version}`,
    "get",
  );
}

export async function signOffNote(noteId: string): Promise<NoteDetail | null> {
  const tz = getTimezone();
  // Throws on a refusal so the caller can undo its optimistic update and show
  // the server's reason ("Only the note's author can sign it").
  return await requestData<NoteDetail>(
    `notes/${noteId}/sign-off`,
    "post",
    { searchParams: { tz } },
    false,
  );
}

/** People a sign-off request for this note can go to, under its template's rule. */
export async function getNoteSignOffSigners(
  noteId: string,
): Promise<NoteSignOffCandidate[] | null> {
  return await requestData<NoteSignOffCandidate[]>(
    `notes/${noteId}/sign-off/signers`,
    "get",
  );
}

/** Ask one person to sign this note. Throws so the caller can show the server's reason. */
export async function requestNoteSignOff(
  noteId: string,
  providerId: string,
  message?: string,
): Promise<NoteDetail | null> {
  const tz = getTimezone();
  return await requestData<NoteDetail>(
    `notes/${noteId}/sign-off/request`,
    "post",
    { json: { providerId, message: message || null }, searchParams: { tz } },
    false,
  );
}

export async function bulkSignOffNotes(
  request: NoteBulkSignOffRequest,
): Promise<NoteBulkSignOffResult | null> {
  return await requestData<NoteBulkSignOffResult>(
    "notes/bulk-sign-off",
    "post",
    {
      json: request,
    },
  );
}

export async function togglePinNote(
  noteId: string,
): Promise<{ id: string; isPinned: boolean } | null> {
  return await requestData<{ id: string; isPinned: boolean }>(
    `notes/${noteId}/pin`,
    "patch",
  );
}

export interface NoteCompletionResult {
  id: string;
  completedDate?: string | null;
  completedByProviderId?: string | null;
}

export async function setNoteCompletion(
  noteId: string,
  isComplete: boolean,
): Promise<NoteCompletionResult | null> {
  return await requestData<NoteCompletionResult>(
    `notes/${noteId}/completion`,
    "patch",
    { json: { isComplete } },
  );
}

export async function getNoteTemplates(): Promise<
  NoteTemplateTableItem[] | null
> {
  return await requestData<NoteTemplateTableItem[]>(`notes/templates`, "get");
}

/** The groups a template's sign-off rule can name, with who is in each now. */
export async function getSignOffSignerGroups(): Promise<
  NoteSignOffSignerGroup[] | null
> {
  return await requestData<NoteSignOffSignerGroup[]>(
    "notes/templates/sign-off-signers",
    "get",
  );
}

/** Who has auto sign-off on, with their roles, for the template's sign-off window. */
export async function getAutoSignOffProviders(): Promise<
  NoteAutoSignOffProvider[] | null
> {
  return await requestData<NoteAutoSignOffProvider[]>(
    "notes/templates/auto-sign-off",
    "get",
  );
}

export async function getNoteTemplateDetail(
  templateId: string,
): Promise<NoteTemplateTableItem | null> {
  return await requestData<NoteTemplateTableItem>(
    `notes/templates/${templateId}`,
    "get",
  );
}

export async function getNoteGroups(
  groupBy: "patientId" | "templateId" | "providerId",
  params: NotesFetchParams,
): Promise<NoteGroupItem[] | null> {
  const tz = getTimezone();
  return await requestData<NoteGroupItem[]>(`notes/groups`, "get", {
    searchParams: { tz, groupBy, ...params },
  });
}

export async function createNote(note: NoteCreate): Promise<NoteDetail | null> {
  return await requestData<NoteDetail>(
    "notes/create",
    "post",
    {
      json: note,
    },
    false,
  );
}

export async function combineAudioFiles(
  noteId: string,
): Promise<boolean | null> {
  return await requestData<boolean>(`notes/${noteId}/combine-audio`, "post");
}

export async function archiveNote(
  noteId?: string,
  noteTranscriptId?: string,
): Promise<boolean | null> {
  if (!noteId && !noteTranscriptId) {
    throw new Error("At least one of noteId or noteTranscriptId is mandatory");
  }

  return await requestData<boolean>("notes/archive", "post", {
    searchParams: {
      noteId,
      noteTranscriptId,
    },
  });
}

export async function createNoteTemplate(
  note: NoteTemplateCreate,
): Promise<NoteTemplateTableItem | null> {
  return await requestData<NoteTemplateTableItem>(
    "notes/templates",
    "post",
    {
      json: note,
    },
    false,
  );
}

export async function deleteNoteTemplate(
  noteTemplateId: string,
): Promise<NoteTemplateTableItem | null> {
  return await requestData<NoteTemplateTableItem>(
    `notes/templates/${noteTemplateId}`,
    "delete",
  );
}

export async function updateNoteTemplate(
  note: NoteTemplateUpdate,
): Promise<NoteTemplateTableItem | null> {
  return await requestData<NoteTemplateTableItem>(
    "notes/templates",
    "put",
    {
      json: note,
    },
    false,
  );
}

export async function toggleNoteTemplateFavorite(
  noteTemplateId: string,
): Promise<NoteTemplateTableItem | null> {
  return await requestData<NoteTemplateTableItem>(
    `notes/templates/${noteTemplateId}/favorite`,
    "post",
  );
}

export async function uploadTemplateAttachment(
  file: File,
): Promise<TemplateAttachmentUploadResponse | null> {
  const formData = new FormData();
  formData.append("attachment", file);

  return await requestData<TemplateAttachmentUploadResponse>(
    "notes/templates/upload-attachment",
    "post",
    {
      body: formData,
    },
  );
}

export async function uploadPlaceholderImage(
  questionId: string,
  file: File,
): Promise<TemplateAttachmentUploadResponse | null> {
  const formData = new FormData();
  formData.append("file", file);

  return await requestData<TemplateAttachmentUploadResponse>(
    `notes/templates/questions/${questionId}/placeholders/upload`,
    "post",
    {
      body: formData,
    },
  );
}

export async function getSignedPreview(
  filePath: string,
): Promise<SignedPreviewResponse | null> {
  return await requestData<SignedPreviewResponse>(
    "forms/document/signed-preview",
    "get",
    {
      searchParams: { filePath },
    },
  );
}

export async function addNoteAttachmentFromUrl(
  noteId: string,
  gcsPath: string,
  answerId: string, // Required for SCRIBE_ATTACHMENTS questions when converting placeholders
  name?: string,
): Promise<NoteAttachmentResponse | null> {
  return await requestData<NoteAttachmentResponse>(
    `notes/${noteId}/attachment-from-url`,
    "post",
    {
      json: {
        gcsPath: gcsPath,
        name: name,
        answerId: answerId,
      },
    },
  );
}

export async function addNoteAttachment(
  patientId: string,
  attachment: File,
  noteId?: string | null,
  name?: string,
  answerId?: string | null, // For SCRIBE_ATTACHMENTS questions
): Promise<NoteAttachmentResponse | null> {
  const formData = new FormData();
  formData.append("attachment", attachment);
  formData.append("patient_id", patientId);
  if (noteId) {
    formData.append("note_id", noteId);
  }
  if (name) {
    formData.append("name", name);
  }
  if (answerId) {
    formData.append("answer_id", answerId);
  }

  return await requestData<NoteAttachmentResponse>(
    `attachments`,
    "post",
    {
      body: formData,
    },
    true,
  );
}

export async function saveUrlToGallery(
  sourceUrl: string,
  patientId: string,
  name?: string,
): Promise<NoteAttachmentResponse | null> {
  return await requestData<NoteAttachmentResponse>(
    `attachments/from-url`,
    "post",
    {
      json: {
        source_url: sourceUrl,
        patient_id: patientId,
        name: name || undefined,
      },
    },
    false,
  );
}

export interface BulkAttachmentUploadResponse {
  attachments: NoteAttachment[];
}

// ── Direct-to-GCS upload (signed URL flow) ─────────────────────────────

export interface InitiateUploadFile {
  filename: string;
  contentType: string;
  name?: string;
  answerId?: string;
}

export interface BulkInitiateUploadRequest {
  patientId: string;
  noteId?: string | null;
  files: InitiateUploadFile[];
}

export interface InitiateUploadResponseItem {
  attachmentId: string;
  uploadUrl: string;
  gcsPath: string;
}

export interface BulkInitiateUploadResponse {
  uploads: InitiateUploadResponseItem[];
}

/** Throws on an error response so callers can tell a permission refusal (403) apart. */
export async function bulkInitiateAttachmentUpload(
  request: BulkInitiateUploadRequest,
): Promise<BulkInitiateUploadResponse | null> {
  return await requestData<BulkInitiateUploadResponse>(
    "attachments/bulk-initiate-upload",
    "post",
    {
      json: {
        patient_id: request.patientId,
        note_id: request.noteId ?? null,
        files: request.files.map((f) => ({
          filename: f.filename,
          content_type: f.contentType,
          name: f.name,
          answer_id: f.answerId,
        })),
      },
    },
    false,
  );
}

export async function bulkFinalizeAttachmentUpload(
  attachmentIds: string[],
): Promise<BulkAttachmentUploadResponse | null> {
  const response = await requestData<BulkAttachmentUploadResponse>(
    "attachments/bulk-finalize-upload",
    "post",
    {
      json: { attachment_ids: attachmentIds },
    },
  );
  return response;
}

/**
 * Upload a file directly to GCS using a signed PUT URL.
 * Uses XMLHttpRequest for upload progress tracking (fetch doesn't support it).
 */
export function uploadToGcs(
  url: string,
  file: File,
  contentType: string,
  onProgress?: (loaded: number, total: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);

    if (signal) {
      signal.addEventListener("abort", () => {
        xhr.abort();
        reject(new DOMException("Upload aborted", "AbortError"));
      });
    }

    if (onProgress) {
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) onProgress(e.loaded, e.total);
      });
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed with status ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.send(file);
  });
}

// The server takes at most this many per request (`LinkAttachmentsToNoteRequest`).
const LINK_ATTACHMENTS_BATCH = 50;

export async function linkAttachmentsToNote(
  noteId: string,
  attachmentIds: string[],
): Promise<NoteAttachment[] | null> {
  const linked: NoteAttachment[] = [];
  for (let i = 0; i < attachmentIds.length; i += LINK_ATTACHMENTS_BATCH) {
    const batch = await requestData<NoteAttachment[]>(
      `attachments/link-to-note`,
      "post",
      {
        json: {
          noteId,
          attachmentIds: attachmentIds.slice(i, i + LINK_ATTACHMENTS_BATCH),
        },
      },
    );
    linked.push(...(batch ?? []));
  }
  return linked;
}

export async function updateNoteAttachmentName(
  attachmentId: string,
  name: string,
): Promise<boolean | null> {
  return await requestData<boolean>(`attachments/${attachmentId}/name`, "put", {
    json: { name },
  });
}

export async function updateAttachmentPortalSharing(
  attachmentId: string,
  visibleToPatient: boolean,
  patientCaption?: string | null,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `attachments/${attachmentId}/patient-portal`,
    "put",
    {
      json: {
        visibleToPatient,
        ...(patientCaption !== undefined
          ? { patientCaption: patientCaption }
          : {}),
      },
    },
    // catchError=false so a failed share/unshare rejects the mutation and shows
    // the error toast instead of silently resolving null and looking successful.
    false,
  );
}

export interface BulkAttachmentNameUpdate {
  attachmentId: string;
  name: string;
}

export async function bulkUpdateNoteAttachmentNames(
  updates: BulkAttachmentNameUpdate[],
): Promise<boolean | null> {
  return await requestData<boolean>(`attachments/bulk-names`, "put", {
    json: { updates },
  });
}

export async function getNoteAttachments(
  noteId: string,
  controller?: AbortController,
): Promise<NoteAttachment[] | null> {
  const tz = getTimezone();
  return await requestData<NoteAttachmentResponse[]>(`attachments`, "get", {
    signal: controller?.signal,
    searchParams: {
      tz,
      note_id: noteId,
    },
  });
}

export async function getNoteAttachment(
  attachmentId: string,
): Promise<NoteAttachment | null> {
  return await requestData<NoteAttachmentResponse>(
    `attachments/${attachmentId}`,
    "get",
    undefined,
    false,
  );
}

export async function applyAttachmentAnnotationsDiff(
  attachmentId: string,
  diff: {
    created: Record<string, any>[];
    updated: Record<string, any>[];
    deletedIds: string[];
    locationId?: string;
  },
): Promise<NoteAttachment | null> {
  return await requestData<NoteAttachmentResponse>(
    `attachments/${attachmentId}/annotations/apply-diff`,
    "post",
    {
      json: diff,
    },
  );
}

export async function getAttachmentAnnotationHistory(
  attachmentId: string,
): Promise<NoteAttachment[] | null> {
  return await requestData<NoteAttachment[]>(
    `attachments/${attachmentId}/annotation-history`,
    "get",
  );
}

export async function restoreAttachmentAnnotations(
  attachmentId: string,
  historyId: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `attachments/${attachmentId}/restore-annotations/${historyId}`,
    "post",
  );
}

export async function deleteNoteAttachment(
  attachmentId: string,
): Promise<DetachedChartDoses | null> {
  // Throw on failure so callers don't report a refused delete as a success.
  return await requestData<DetachedChartDoses>(
    `attachments/${attachmentId}`,
    "delete",
    undefined,
    false,
  );
}

export async function unlinkAttachmentFromNote(
  attachmentId: string,
  noteId: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `attachments/${attachmentId}/unlink/${noteId}`,
    "delete",
  );
}

export async function duplicateNoteAttachment(
  attachmentId: string,
  targetNoteId?: string,
): Promise<NoteAttachment | null> {
  const params = targetNoteId
    ? `?target_note_id=${encodeURIComponent(targetNoteId)}`
    : "";
  return await requestData<NoteAttachment>(
    `attachments/${attachmentId}/duplicate${params}`,
    "post",
  );
}

export async function replaceAttachmentImage(
  attachmentId: string,
  file: File,
): Promise<NoteAttachment | null> {
  const formData = new FormData();
  formData.append("file", file);

  return await requestData<NoteAttachment>(
    `attachments/${attachmentId}/image`,
    "put",
    {
      body: formData,
    },
  );
}

/**
 * A lot number recovered from past doses, for the dose lot picker's off-inventory list.
 * There is no shipment behind these, so they carry how the lot was last used instead of
 * a shipment's quantity/location/supplier.
 *
 * Served by `/notes/lot-number-history`, not `/notes/lot-numbers` — the latter is a
 * documented external endpoint whose bare-string response we do not change.
 */
export interface LotNumberHistory {
  lotNumber: string;
  /** The medication the most recent dose from this lot administered. */
  medicationId?: string | null;
  medicationName?: string | null;
  category?: string | null;
  lastUsedDate?: string | null;
  doseCount: number;
  amount?: number | null;
  unit?: Unit | null;
}

export async function getLotNumbers(params?: {
  medicationId?: string;
  locationId?: string;
}): Promise<LotNumberHistory[] | null> {
  return await requestData<LotNumberHistory[]>(
    "notes/lot-number-history",
    "get",
    {
      searchParams: {
        medicationId: params?.medicationId,
        locationId: params?.locationId,
      },
    },
  );
}

export async function getAttachments(params: {
  patientId?: string;
  date?: string;
  tagIds?: string[];
  optedInToSocialMedia?: boolean;
  search?: string;
  controller?: AbortController;
}): Promise<AttachmentDayPage | null> {
  const tz = getTimezone();
  return await requestData<AttachmentDayPage>(`attachments`, "get", {
    signal: params.controller?.signal,
    searchParams: {
      tz,
      ...(params.patientId ? { patient_id: params.patientId } : {}),
      ...(params.date ? { date: params.date } : {}),
      ...(params.tagIds && params.tagIds.length > 0
        ? { tagIds: params.tagIds }
        : {}),
      ...(params.optedInToSocialMedia !== undefined
        ? { optedInToSocialMedia: params.optedInToSocialMedia }
        : {}),
      ...(params.search ? { search: params.search } : {}),
    },
    timeout: 10 * 60 * 1000, // 10 minutes
  });
}

export async function getNoteAttachmentTags(): Promise<Page<NoteAttachmentTag> | null> {
  return await requestData<Page<NoteAttachmentTag>>("attachments/tags", "get");
}

export async function createNoteAttachmentTag(
  name: string,
  color: string = "#3B82F6",
): Promise<NoteAttachmentTag | null> {
  return await requestData<NoteAttachmentTag>("attachments/tags", "post", {
    json: { name, color },
  });
}

export async function updateNoteAttachmentTag(
  tagId: string,
  data: { name?: string; color?: string },
): Promise<NoteAttachmentTag | null> {
  return await requestData<NoteAttachmentTag>(
    `attachments/tags/${tagId}`,
    "put",
    {
      json: data,
    },
  );
}

export async function deleteNoteAttachmentTag(
  tagId: string,
): Promise<{ message: string } | null> {
  return await requestData<{ message: string }>(
    `attachments/tags/${tagId}`,
    "delete",
  );
}

export async function assignNoteAttachmentTags(
  attachmentId: string,
  tagIds: string[],
): Promise<{ message: string } | null> {
  return await requestData<{ message: string }>(
    `attachments/${attachmentId}/tags`,
    "post",
    {
      json: { tagIds },
    },
  );
}

export async function bulkAssignNoteAttachmentTags(
  attachmentIds: string[],
  tagIds: string[],
): Promise<{ message: string } | null> {
  return await requestData<{ message: string }>(
    `attachments/bulk-tags`,
    "post",
    {
      json: { attachmentIds, tagIds },
    },
  );
}

export async function changeAttachmentPatient(
  attachmentId: string,
  patientId: string,
): Promise<DetachedChartDoses | null> {
  return await requestData<DetachedChartDoses>(
    `attachments/${attachmentId}/patient`,
    "put",
    {
      json: { patientId },
    },
  );
}

/**
 * Get pre-filled answers for a template based on patient's existing data.
 * Returns answer data that can be used to pre-populate note answers when creating a new note.
 */
export async function getPrefilledAnswers(
  templateId: string,
  patientId: string,
): Promise<PrefilledAnswersResponse | null> {
  return await requestData<PrefilledAnswersResponse>(
    `notes/templates/${templateId}/prefill/${patientId}`,
    "get",
  );
}

/**
 * List all active dynamic fields available for template configuration.
 * Returns both predefined fields (with type like ALLERGIES) and custom fields (with type=null).
 */
export async function getDynamicFields(): Promise<DynamicFieldSummary[]> {
  return (
    (await requestData<DynamicFieldSummary[]>(
      "notes/templates/dynamic-fields",
      "get",
    )) ?? []
  );
}

/**
 * Re-run the AI task suggester for a note's revision chain.
 *
 * Synchronous on the server — the pass is one bounded call, so the new set comes
 * straight back rather than needing a poll. Accepted and dismissed suggestions
 * are preserved; only undecided ones are replaced.
 */
export async function regenerateNoteTaskSuggestions(
  noteId: string,
): Promise<NoteTaskSuggestionSet | null> {
  // catchError=false so a failure throws instead of resolving to null: the
  // caller shows an error toast from its catch, and without this a failed
  // generate returned null, skipped the catch, and still refreshed the note —
  // reporting failure as success.
  return await requestData<NoteTaskSuggestionSet>(
    `notes/${noteId}/task-suggestions/generate`,
    "post",
    { timeout: 60 * 1000 * 2 },
    false,
  );
}

/** Hide one AI task suggestion. Idempotent. */
export async function dismissNoteTaskSuggestion(
  noteId: string,
  suggestionId: string,
): Promise<NoteTaskSuggestionSet | null> {
  // Throws on failure — see regenerateNoteTaskSuggestions above.
  return await requestData<NoteTaskSuggestionSet>(
    `notes/${noteId}/task-suggestions/${suggestionId}/dismiss`,
    "post",
    undefined,
    false,
  );
}
